import ScrollToTop from 'components/ScrollToTop/ScrollToTop'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useMediaQuery } from 'react-responsive'
import Footer from 'views/LatestHomePage/components/Footer'
import { NavBar } from 'views/LatestHomePage/components/NavBar'

const SMSTerms = () => {
  const isMobile = useMediaQuery({ maxWidth: 639 })
  const [activeIndex, setActiveIndex] = useState(null)

  const sections = [
    { title: '1. Program Description', id: 'program-description' },
    { title: '2. How You Opt In', id: 'opt-in' },
    { title: '3. Message Frequency', id: 'message-frequency' },
    { title: '4. Cost', id: 'cost' },
    { title: '5. Opting Out', id: 'opting-out' },
    { title: '6. Help', id: 'help' },
    { title: '7. Carriers', id: 'carriers' },
    { title: '8. Privacy', id: 'privacy' },
    { title: '9. Changes to These Terms', id: 'changes' },
  ]

  const handleScrollToSection = (id, index) => {
    setActiveIndex(index)

    const sectionElement = document.getElementById(id)

    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <>
      <Helmet>
        <title>SMS Terms & Conditions | Remote Recruit</title>
        <meta
          name="description"
          content="Read the SMS Terms & Conditions for Remote Recruit's booking notification text messaging program."
        />
      </Helmet>

      <div className="flex flex-col items-center justify-center min-h-full bg-white">
        <NavBar showHowItWorks={false} />

        <img
          className="w-full h-[365.833px] sm:h-[528.878px] object-cover"
          src={
            isMobile
              ? 'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/privacy_terms_mobile_bg.png'
              : 'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/privacy_terms_bg.png'
          }
          alt="SMS Terms Background"
          loading="lazy"
        />

        <div className="flex flex-col items-center justify-center absolute">
          <ScrollToTop />

          <h1 className="text-[#FFC628] text-center font-poppins text-[36px] sm:text-[72px] not-italic leading-[35.64px] sm:leading-[71.28px] font-semibold">
            SMS Terms & Conditions
          </h1>

          <p className="text-white text-center font-manrope text-[16px] sm:text-[20px] not-italic font-semibold leading-[29px] opacity-80 mt-[10px]">
            Effective date: June 8, 2026
          </p>
        </div>
      </div>

      <div className="sm:grid sm:grid-cols-3 gap-6 z-20">
        <div className="hidden sm:block col-span-1 ml-[96px] font-manrope border-t-[8.804px] border-t-[#1E3E85] gap-[8.408px] w-[265px] h-[353px] overflow-y-auto flex-shrink-0 rounded-[8.408px] bg-white shadow-box-shadow-custom">
          {sections.map((section, index) => (
            <p
              key={section.id}
              onClick={() => handleScrollToSection(section.id, index)}
              className={`p-[8px] ${
                activeIndex === index ? 'bg-[#EFF0F8]' : ''
              } hover:bg-[#EFF0F8] cursor-pointer`}
            >
              {section.title}
            </p>
          ))}
        </div>

        <div className="col-span-2 sm:w-5/6 sm:px-0 px-6">
          <section>
            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7 mb-8">
              <p>
                These SMS Terms & Conditions govern the RemoteRecruit Booking
                Notification text messaging program (the "Program") operated by
                Remote Recruit, Inc. ("RemoteRecruit," "we," "us," or "our"). By
                providing your mobile phone number and agreeing to receive text
                messages from us, you accept these terms.
              </p>
            </div>
          </section>

          <section id="program-description">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              1. Program Description
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                When you contact RemoteRecruit by phone and request to schedule
                a meeting, you may choose to receive your meeting booking link
                or calendar invitation by SMS text message. The Program is used
                solely to deliver that requested booking link or confirmed
                meeting details to the mobile number you provide during the
                call. We do not send marketing or promotional messages through
                this Program.
              </p>
            </div>
          </section>

          <section className="mt-10" id="opt-in">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              2. How You Opt In (Call to Action)
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                Enrollment is initiated by you during an inbound phone call that
                you place to RemoteRecruit. During the call, if you ask to book
                a meeting, you are offered three options: receive your booking
                link by SMS, receive it by email, or be transferred to a team
                member. If you choose SMS, our assistant reads the following
                disclosure aloud, and you must verbally agree and provide your
                mobile number before any text is sent:
              </p>

              <blockquote className="my-4 px-5 py-4 bg-[#fbfbf9] border-l-[3px] border-l-[#1E3E85] italic text-[#33383F]">
                “I’ll text you a link from RemoteRecruit to book your meeting.
                You’ll receive a one-time message for this request. Message and
                data rates may apply. Reply <strong>HELP</strong> for help or{' '}
                <strong>STOP</strong> to opt out at any time. Is it okay to text
                you at this number?”
              </blockquote>

              <p>
                Only callers who verbally agree and provide their own mobile
                number receive a text. We never obtain phone numbers from any
                other source, and we do not send recurring, marketing, or
                promotional messages through this Program.
              </p>
            </div>
          </section>

          <section className="mt-10" id="message-frequency">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              3. Message Frequency
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                Message frequency is limited to one-time messages sent per
                request. You will generally receive a single text containing
                your booking link, and where applicable a follow-up text
                confirming your scheduled meeting. We do not send recurring
                messages.
              </p>
            </div>
          </section>

          <section className="mt-10" id="cost">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              4. Cost
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                <strong>Message and data rates may apply.</strong> RemoteRecruit
                does not charge for the messages, but your mobile carrier's
                standard message and data rates may apply depending on your
                plan. Contact your wireless provider for details about your
                messaging plan.
              </p>
            </div>
          </section>

          <section className="mt-10" id="opting-out">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              5. Opting Out
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                You can cancel the Program at any time by replying{' '}
                <strong>STOP</strong> to any message you receive from us. After
                you send <strong>STOP</strong>, we will send a single
                confirmation message and will not send you any further texts
                unless you re-enroll by requesting messages again.
              </p>
            </div>
          </section>

          <section className="mt-10" id="help">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              6. Help
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                For help with the Program, reply <strong>HELP</strong> to any
                message, or contact us at{' '}
                <a
                  href="mailto:team@remoterecruit.com"
                  className="text-[#1E3E85]"
                >
                  team@remoterecruit.com
                </a>
                . For support you can also email us at the same address at any
                time.
              </p>
            </div>
          </section>

          <section className="mt-10" id="carriers">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              7. Carriers
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                Wireless carriers are not liable for delayed or undelivered
                messages. Message delivery is subject to effective transmission
                by your wireless carrier and is not guaranteed.
              </p>
            </div>
          </section>

          <section className="mt-10" id="privacy">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              8. Privacy
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7">
              <p>
                The mobile information you provide for this Program — including
                your phone number and your consent to receive texts — is used
                only to deliver your requested booking link or calendar
                invitation. This information is{' '}
                <strong>
                  not shared with, sold to, or disclosed to any third parties or
                  affiliates for marketing or promotional purposes.
                </strong>{' '}
                For more detail on how we handle your information, see our{' '}
                <a
                  href="https://remoterecruit.com/privacy-policy"
                  className="text-[#1E3E85] underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </section>

          <section className="mt-10" id="changes">
            <p className="text-[#1B1D21] text-[24px] leading-[26px] font-semibold mb-[15px]">
              9. Changes to These Terms
            </p>

            <div className="text-[#6D6E7A] font-poppins text-base font-normal leading-7 mb-[114px]">
              <p>
                We may update these SMS Terms & Conditions from time to time.
                Revised terms are effective when posted on this page.
              </p>

              <p className="mt-8">
                <strong>Remote Recruit, Inc.</strong>
                <br />
                Questions:{' '}
                <a
                  href="mailto:team@remoterecruit.com"
                  className="text-[#1E3E85]"
                >
                  team@remoterecruit.com
                </a>
                <br />© {new Date().getFullYear()} Remote Recruit. All Rights
                Reserved.
              </p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </>
  )
}

export default SMSTerms
